import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters, format } from 'date-fns';
import {
  calculateCompanyKPIs,
  buildProgressMap,
  buildPlannedVolumeBySite,
  buildTheoreticalVolumeBySite,
  buildDeliveredVolumeBySite,
  WorkItemData,
  ProgressData,
  PourWorkItemData,
  FormulaData,
  SupplierOptionData,
} from '@/lib/kpiCalculations';
import { fetchAuthorizedSiteIds } from '@/hooks/queries/useAuthorizedSiteIds';
import { calculateTrend } from '@/lib/trendCalculations';

export interface EnrichedStats {
  totalActiveSites: number;
  totalVolumeDelivered: number;
  conformityRate: number;
  openIncidents: number;
  averageProgress: number;
  plannedBudget: number;
  actualBudget: number;
  sitesTrend: number;
  volumeTrend: number;
  conformityTrend: number;
  incidentsTrend: number;
  averageDeviation: number;
  /** Previous period absolute values for "vs XXX" display */
  previousVolume: number;
  previousConformity: number;
  previousIncidents: number;
}

export interface SitePerformance {
  id: string;
  short_code: string | null;
  name: string;
  location: string;
  siteManagerName: string | null;
  supervisorName: string | null;
  progress: number;
  volumeDelivered: number;
  volumePlanned: number;
  volumeTheoretical: number;
  volumeDeviation: number;
  deviationPercent: number;
  incidents: number;
  openIncidents: number;
  conformity: number;
  score: number;
  lastPourDate: string | null;
}

export interface IncidentDistribution {
  type: string;
  label: string;
  count: number;
  color: string;
  trend: number;
}

export interface Alert {
  id: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  siteId?: string;
  action?: string;
  timestamp: Date;
}

export interface VolumeTrend {
  week: string;
  planned: number;
  actual: number;
}

export interface BudgetTrend {
  week: string;
  expense: number;
  cumulative: number;
}

interface DashboardEnrichedData {
  enrichedStats: EnrichedStats;
  sitesPerformance: SitePerformance[];
  incidentsDistribution: IncidentDistribution[];
  topSites: SitePerformance[];
  alerts: Alert[];
  volumeTrends: VolumeTrend[];
  budgetTrends: BudgetTrend[];
}

function getPeriodRanges(period: 'semaine' | 'mois' | 'trimestre') {
  const now = new Date();

  switch (period) {
    case 'semaine':
      return {
        current: { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) },
        previous: { start: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }), end: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }) },
        weeksCount: 1
      };
    case 'mois':
      return {
        current: { start: startOfMonth(now), end: endOfMonth(now) },
        previous: { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) },
        weeksCount: 4
      };
    case 'trimestre':
      return {
        current: { start: startOfQuarter(now), end: endOfQuarter(now) },
        previous: { start: startOfQuarter(subQuarters(now, 1)), end: endOfQuarter(subQuarters(now, 1)) },
        weeksCount: 12
      };
  }
}

const defaultStats: EnrichedStats = {
  totalActiveSites: 0,
  totalVolumeDelivered: 0,
  conformityRate: 100,
  openIncidents: 0,
  averageProgress: 0,
  plannedBudget: 0,
  actualBudget: 0,
  sitesTrend: 0,
  volumeTrend: 0,
  conformityTrend: 0,
  incidentsTrend: 0,
  averageDeviation: 0,
  previousVolume: 0,
  previousConformity: 0,
  previousIncidents: 0,
};

export async function fetchDashboardEnrichedData(
  companyId: string,
  userId: string,
  role: string | undefined,
  period: 'semaine' | 'mois' | 'trimestre'
): Promise<DashboardEnrichedData> {
  const ranges = getPeriodRanges(period);
  const { current, previous, weeksCount } = ranges;

  // Use centralized site access logic
  const { siteIds: authorizedSiteIds } = await fetchAuthorizedSiteIds(userId, role);
  let siteIds: string[] | null = role === 'company_director' ? null : authorizedSiteIds;

  if (siteIds !== null && siteIds.length === 0) {
    return {
      enrichedStats: defaultStats,
      sitesPerformance: [],
      incidentsDistribution: [],
      topSites: [],
      alerts: [],
      volumeTrends: [],
      budgetTrends: [],
    };
  }

  // Fetch ALL data in parallel
  let sitesQuery = supabase
    .from('sites')
    .select('id, name, location, status, short_code')
    .eq('company_id', companyId)
    .in('status', ['in_progress', 'planning']);

  if (siteIds) {
    sitesQuery = sitesQuery.in('id', siteIds);
  }

  const [
    sitesData,
    currentDeliveriesData,
    previousDeliveriesData,
    currentIncidentsData,
    previousIncidentsData,
    budgetData,
  ] = await Promise.all([
    sitesQuery,
    supabase
      .from('concrete_deliveries')
      .select(`
        id, delivered_volume, ordered_volume, status, created_at,
        concrete_pours!inner(site_id, delivery_date, sites!inner(company_id)),
        delivery_incidents(id, resolved)
      `)
      .eq('concrete_pours.sites.company_id', companyId)
      .gte('concrete_pours.delivery_date', current.start.toISOString().split('T')[0])
      .lte('concrete_pours.delivery_date', current.end.toISOString().split('T')[0]),
    supabase
      .from('concrete_deliveries')
      .select(`
        id, delivered_volume, ordered_volume, status, created_at,
        concrete_pours!inner(site_id, delivery_date, sites!inner(company_id)),
        delivery_incidents(id, resolved)
      `)
      .eq('concrete_pours.sites.company_id', companyId)
      .gte('concrete_pours.delivery_date', previous.start.toISOString().split('T')[0])
      .lte('concrete_pours.delivery_date', previous.end.toISOString().split('T')[0]),
    supabase
      .from('delivery_incidents')
      .select(`
        id, incident_type, severity_level, resolved, created_at,
        concrete_deliveries!truck_id(id, concrete_pours!inner(site_id, sites!inner(company_id, name)))
      `)
      .eq('concrete_deliveries.concrete_pours.sites.company_id', companyId)
      .gte('created_at', current.start.toISOString())
      .lte('created_at', current.end.toISOString()),
    supabase
      .from('delivery_incidents')
      .select(`
        id, incident_type, severity_level, resolved, created_at,
        concrete_deliveries!truck_id(id, concrete_pours!inner(site_id, sites!inner(company_id, name)))
      `)
      .eq('concrete_deliveries.concrete_pours.sites.company_id', companyId)
      .gte('created_at', previous.start.toISOString())
      .lte('created_at', previous.end.toISOString()),
    supabase
      .from('order_formulas')
      .select(`volume, unit_price_m3, order_id, global_orders!inner(site_id, sites!inner(company_id))`)
      .eq('global_orders.sites.company_id', companyId),
  ]);

  if (sitesData.error) throw sitesData.error;

  const sites = sitesData.data || [];
  if (!siteIds) {
    siteIds = sites.map(s => s.id);
  }

  // Fetch work_items, pour_work_items, work_progress, order_supplier_options, and site_assignments
  const [workItemsResult, pourWorkItemsResult, workProgressResult, optionsResult, assignmentsResult, latestPoursResult] = await Promise.all([
    supabase.from('work_items').select('id, volume, site_id').in('site_id', siteIds),
    // pour_work_items: only count pours that are in_progress or completed
    supabase
      .from('pour_work_items')
      .select('work_item_id, volume_poured, actual_volume_poured, concrete_pours!inner(site_id, status)')
      .in('concrete_pours.site_id', siteIds)
      .in('concrete_pours.status', ['in_progress', 'completed']),
    // work_progress: user-entered progress
    supabase
      .from('work_progress')
      .select('work_item_id, progress_percentage, site_id')
      .in('site_id', siteIds),
    // order_supplier_options: full budget (formulas + transport + taxes)
    supabase
      .from('order_supplier_options')
      .select(`order_id, category, unit_type, price, active, global_orders!inner(site_id)`)
      .in('global_orders.site_id', siteIds)
      .eq('active', true),
    // site_assignments: site manager + project supervisor
    supabase
      .from('site_assignments')
      .select('site_id, role, profiles!user_id(first_name, last_name)')
      .in('site_id', siteIds)
      .in('role', ['site_manager', 'project_supervisor']),
    // latest concrete_pour date per site (exclude drafts and cancelled)
    supabase
      .from('concrete_pours')
      .select('site_id, delivery_date')
      .in('site_id', siteIds)
      .not('status', 'in', '("draft","cancelled")')
      .is('deleted_at', null)
      .lte('delivery_date', new Date().toISOString().split('T')[0])
      .order('delivery_date', { ascending: false }),
  ]);

  const currentDeliveries = currentDeliveriesData.data || [];
  const previousDeliveries = previousDeliveriesData.data || [];
  const currentIncidents = currentIncidentsData.data || [];
  const prevIncidents = previousIncidentsData.data || [];
  const budgetFormulas = budgetData.data || [];
  const workItems = workItemsResult.data || [];
  const pourWorkItems = pourWorkItemsResult.data || [];
  const workProgressData = workProgressResult.data || [];
  const optionsData = optionsResult.data || [];
  const assignments = assignmentsResult.data || [];
  const latestPours = latestPoursResult.data || [];

  // Build last pour date map (first occurrence per site since ordered desc)
  const lastPourDateBySite = new Map<string, string>();
  latestPours.forEach((p: any) => {
    if (p.site_id && p.delivery_date && !lastPourDateBySite.has(p.site_id)) {
      lastPourDateBySite.set(p.site_id, p.delivery_date);
    }
  });

  // Build manager/supervisor maps from assignments
  const managerBySite = new Map<string, string>();
  const supervisorBySite = new Map<string, string>();
  assignments.forEach((a: any) => {
    const profile = a.profiles;
    const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : null;
    if (!name) return;
    if (a.role === 'site_manager' && !managerBySite.has(a.site_id)) {
      managerBySite.set(a.site_id, name);
    }
    if (a.role === 'project_supervisor' && !supervisorBySite.has(a.site_id)) {
      supervisorBySite.set(a.site_id, name);
    }
  });

  // Create Maps for O(1) access
  const deliveriesBySite = new Map<string, any[]>();
  const incidentsBySite = new Map<string, any[]>();

  currentDeliveries.forEach(l => {
    const sid = l.concrete_pours?.site_id;
    if (sid) {
      if (!deliveriesBySite.has(sid)) deliveriesBySite.set(sid, []);
      deliveriesBySite.get(sid)!.push(l);
    }
  });

  currentIncidents.forEach(i => {
    const sid = i.concrete_deliveries?.concrete_pours?.site_id;
    if (sid) {
      if (!incidentsBySite.has(sid)) incidentsBySite.set(sid, []);
      incidentsBySite.get(sid)!.push(i);
    }
  });

  // Transform data to centralized KPI format
  const workItemsForKPI: WorkItemData[] = workItems.map(o => ({
    id: o.id,
    volume: Number(o.volume || 0),
    site_id: o.site_id,
  }));

  const progressForKPI: ProgressData[] = workProgressData.map(wp => ({
    work_item_id: wp.work_item_id,
    progress_percentage: wp.progress_percentage,
  }));

  const pourItemsForKPI: PourWorkItemData[] = pourWorkItems.map(pwi => ({
    work_item_id: pwi.work_item_id,
    volume_poured: Number(pwi.volume_poured || 0),
    actual_volume_poured: pwi.actual_volume_poured,
    site_id: pwi.concrete_pours?.site_id,
  }));

  const formulasForKPI: FormulaData[] = budgetFormulas.map(f => ({
    volume: Number(f.volume),
    unit_price_m3: Number(f.unit_price_m3),
    site_id: f.global_orders?.site_id,
  }));

  const optionsForKPI: SupplierOptionData[] = optionsData.map(o => ({
    category: o.category,
    unit_type: o.unit_type,
    price: o.price,
    active: o.active,
    site_id: o.global_orders?.site_id,
  }));

  // Calculate KPIs using centralized functions (same as site budget page)
  const companyKPIs = calculateCompanyKPIs(
    siteIds,
    workItemsForKPI,
    progressForKPI,
    pourItemsForKPI,
    formulasForKPI,
    optionsForKPI
  );

  // Extract values from centralized KPIs
  const totalVolumeDelivered = companyKPIs.totalVolumeDelivered;
  const averageProgress = companyKPIs.averageProgress;
  const plannedBudget = companyKPIs.budgetTotal;

  // Build maps for per-site calculations
  const volumePlannedBySite = buildPlannedVolumeBySite(workItemsForKPI);
  const progressMap = buildProgressMap(progressForKPI);
  const volumeTheoreticalBySite = buildTheoreticalVolumeBySite(workItemsForKPI, progressMap);
  const volumeDeliveredBySite = buildDeliveredVolumeBySite(pourItemsForKPI);

  const currentVolume = currentDeliveries.reduce((sum, l) => sum + (Number(l.delivered_volume) || 0), 0);
  const previousVolume = previousDeliveries.reduce((sum, l) => sum + (Number(l.delivered_volume) || 0), 0);

  const currentDeliveriesWithIncidents = currentDeliveries.filter(l =>
    l.delivery_incidents && l.delivery_incidents.length > 0
  ).length;
  const currentConformity = currentDeliveries.length > 0
    ? ((currentDeliveries.length - currentDeliveriesWithIncidents) / currentDeliveries.length) * 100
    : 100;

  const previousDeliveriesWithIncidents = previousDeliveries.filter(l =>
    l.delivery_incidents && l.delivery_incidents.length > 0
  ).length;
  const previousConformity = previousDeliveries.length > 0
    ? ((previousDeliveries.length - previousDeliveriesWithIncidents) / previousDeliveries.length) * 100
    : 100;

  const currentIncidentsCount = currentIncidents.length;
  const previousIncidentsCount = prevIncidents.length;
  const openIncidents = currentIncidents.filter(i => !i.resolved).length;

  const totalVolumePlanned = budgetFormulas.reduce((s, f) => s + Number(f.volume), 0);
  const averagePrice = totalVolumePlanned > 0 ? plannedBudget / totalVolumePlanned : 100;
  const actualBudget = totalVolumeDelivered * averagePrice;

  // Use currentVolume (filtered by period) instead of global total
  const periodVolumeDelivered = currentVolume;

  // Calculate average deviation across all sites
  // Deviation = (volumeTheoretical - volumeDelivered) / volumeTheoretical * 100
  let averageDeviation = 0;
  const sitesWithDeviation = siteIds.filter(siteId => {
    const volumeTheoretical = volumeTheoreticalBySite.get(siteId) || 0;
    return volumeTheoretical > 0;
  });

  if (sitesWithDeviation.length > 0) {
    const totalDeviation = sitesWithDeviation.reduce((sum, siteId) => {
      const volumeTheoretical = volumeTheoreticalBySite.get(siteId) || 0;
      const volumeDeliveredSite = volumeDeliveredBySite.get(siteId) || 0;
      const deviation = volumeTheoretical > 0 ? ((volumeTheoretical - volumeDeliveredSite) / volumeTheoretical) * 100 : 0;
      return sum + deviation;
    }, 0);
    averageDeviation = totalDeviation / sitesWithDeviation.length;
  }

  const enrichedStats: EnrichedStats = {
    totalActiveSites: sites.length,
    totalVolumeDelivered: periodVolumeDelivered,
    conformityRate: currentConformity,
    openIncidents,
    averageProgress,
    plannedBudget,
    actualBudget,
    sitesTrend: 0,
    volumeTrend: calculateTrend(currentVolume, previousVolume),
    conformityTrend: calculateTrend(currentConformity, previousConformity),
    incidentsTrend: calculateTrend(currentIncidentsCount, previousIncidentsCount),
    averageDeviation,
    previousVolume,
    previousConformity,
    previousIncidents: previousIncidentsCount,
  };

  // Calculate per-site performance
  const sitesPerformance: SitePerformance[] = sites.map(site => {
    const siteDeliveries = deliveriesBySite.get(site.id) || [];
    const siteIncidents = incidentsBySite.get(site.id) || [];
    const volumePlanned = volumePlannedBySite.get(site.id) || 0;
    const volumeTheoretical = volumeTheoreticalBySite.get(site.id) || 0;
    const volumeDelivered = volumeDeliveredBySite.get(site.id) || 0;

    const volumeDeviation = volumeTheoretical - volumeDelivered;
    const deviationPercent = volumeTheoretical > 0 ? (volumeDeviation / volumeTheoretical) * 100 : 0;

    // Progress = volumeTheoretical / volumePlanned * 100 (same formula as budget page)
    const siteProgress = volumePlanned > 0
      ? (volumeTheoretical / volumePlanned) * 100
      : 0;

    const conformity = siteDeliveries.length > 0
      ? ((siteDeliveries.length - siteIncidents.length) / siteDeliveries.length) * 100
      : 100;

    const score =
      (siteProgress / 100) * 40 +
      (1 - Math.min(Math.abs(deviationPercent) / 100, 1)) * 30 +
      (1 - Math.min(siteIncidents.length / 10, 1)) * 20 +
      (conformity / 100) * 10;

    return {
      id: site.id,
      short_code: site.short_code ?? null,
      name: site.name,
      location: site.location || '',
      siteManagerName: managerBySite.get(site.id) || null,
      supervisorName: supervisorBySite.get(site.id) || null,
      progress: siteProgress,
      volumeDelivered,
      volumePlanned,
      volumeTheoretical,
      volumeDeviation,
      deviationPercent,
      incidents: siteIncidents.length,
      openIncidents: siteIncidents.filter(i => !i.resolved).length,
      conformity,
      score,
      lastPourDate: lastPourDateBySite.get(site.id) || null,
    };
  });

  const topSites = [...sitesPerformance].sort((a, b) => b.score - a.score).slice(0, 3);

  // Incident trends by type
  const countByType = (incidents: any[], type: string) =>
    incidents.filter(i => i.incident_type === type).length;

  const incidentsDistribution: IncidentDistribution[] = [
    { type: 'retard', label: 'Retards', count: countByType(currentIncidents, 'retard'), color: 'hsl(var(--warning))', trend: calculateTrend(countByType(currentIncidents, 'retard'), countByType(prevIncidents, 'retard')) },
    { type: 'quantite', label: 'Volumes manquants', count: countByType(currentIncidents, 'quantite'), color: 'hsl(var(--destructive))', trend: calculateTrend(countByType(currentIncidents, 'quantite'), countByType(prevIncidents, 'quantite')) },
    { type: 'qualite', label: 'Non-conformité béton', count: countByType(currentIncidents, 'qualite'), color: 'hsl(var(--chart-2))', trend: calculateTrend(countByType(currentIncidents, 'qualite'), countByType(prevIncidents, 'qualite')) },
    { type: 'autre', label: 'Autres', count: countByType(currentIncidents, 'autre'), color: 'hsl(var(--muted))', trend: calculateTrend(countByType(currentIncidents, 'autre'), countByType(prevIncidents, 'autre')) },
  ];

  // Alerts
  const alerts: Alert[] = [];
  sitesPerformance.forEach(site => {
    if (Math.abs(site.deviationPercent) > 10) {
      alerts.push({
        id: `deviation-${site.id}`,
        type: 'warning',
        message: `Dérive volume sur ${site.name} (${site.deviationPercent > 0 ? '+' : ''}${site.deviationPercent.toFixed(1)}%)`,
        siteId: site.id,
        action: `/sites/${site.short_code ?? site.id}`,
        timestamp: new Date()
      });
    }
    if (site.openIncidents > 3) {
      alerts.push({
        id: `incidents-${site.id}`,
        type: 'error',
        message: `${site.openIncidents} incidents ouverts sur ${site.name}`,
        siteId: site.id,
        action: `/sites/${site.short_code ?? site.id}`,
        timestamp: new Date()
      });
    }
    if (site.conformity < 85) {
      alerts.push({
        id: `conformity-${site.id}`,
        type: 'error',
        message: `Taux de conformité faible sur ${site.name} (${site.conformity.toFixed(0)}%)`,
        siteId: site.id,
        action: `/sites/${site.short_code ?? site.id}`,
        timestamp: new Date()
      });
    }
  });

  // Volume trends — fetch all deliveries (for weekly trends)
  const { data: allDeliveriesData } = await supabase
    .from('concrete_deliveries')
    .select(`id, delivered_volume, ordered_volume, concrete_pours!inner(site_id, delivery_date, sites!inner(company_id))`)
    .eq('concrete_pours.sites.company_id', companyId);

  const allDeliveries = allDeliveriesData || [];
  const volumeByWeek = new Map<string, { planned: number; actual: number }>();

  allDeliveries.forEach(d => {
    const deliveryDate = new Date(d.concrete_pours?.delivery_date);
    const weekStart = startOfWeek(deliveryDate, { weekStartsOn: 1 });
    const weekKey = format(weekStart, 'yyyy-MM-dd');

    if (!volumeByWeek.has(weekKey)) {
      volumeByWeek.set(weekKey, { planned: 0, actual: 0 });
    }
    const week = volumeByWeek.get(weekKey)!;
    week.planned += Number(d.ordered_volume) || 0;
    week.actual += Number(d.delivered_volume) || 0;
  });

  const sortedWeeks = Array.from(volumeByWeek.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-weeksCount);

  const volumeTrends: VolumeTrend[] = sortedWeeks.map(([, data], index) => ({
    week: `S${index + 1}`,
    planned: Math.round(data.planned * 10) / 10,
    actual: Math.round(data.actual * 10) / 10,
  }));

  if (volumeTrends.length === 0) {
    for (let i = 0; i < Math.min(4, weeksCount); i++) {
      volumeTrends.push({ week: `S${i + 1}`, planned: 0, actual: 0 });
    }
  }

  let cumulative = 0;
  const budgetTrends: BudgetTrend[] = volumeTrends.map((vol, index) => {
    const expense = vol.actual * averagePrice;
    cumulative += expense;
    return {
      week: `S${index + 1}`,
      expense: Math.round(expense),
      cumulative: Math.round(cumulative)
    };
  });

  return {
    enrichedStats,
    sitesPerformance,
    incidentsDistribution,
    topSites,
    alerts,
    volumeTrends,
    budgetTrends,
  };
}

export function useDashboardEnriched(period: 'semaine' | 'mois' | 'trimestre' = 'mois') {
  const { user } = useAuth();

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['dashboard-enriched', user?.company_id, user?.id, user?.role, period],
    queryFn: () => fetchDashboardEnrichedData(user!.company_id, user!.id, user?.role, period),
    enabled: !!user?.company_id && !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  return {
    enrichedStats: data?.enrichedStats || defaultStats,
    sitesPerformance: data?.sitesPerformance || [],
    incidentsDistribution: data?.incidentsDistribution || [],
    topSites: data?.topSites || [],
    alerts: data?.alerts || [],
    volumeTrends: data?.volumeTrends || [],
    budgetTrends: data?.budgetTrends || [],
    loading: isLoading,
    isFetching,
    error,
    refetch,
  };
}
